import { Controller } from "@hotwired/stimulus"

/**
 * Clip Audio Preview Controller
 *
 * Plays ONLY the hook window [startMs, endMs] of a saved track and shows a
 * CLIP-RELATIVE clock (0 = the first frame of the rendered short), so the
 * creator can read off the exact ms for each lyric line. Loops the window and
 * exposes a one-tap "copy current ms" so the value can be pasted into a
 * start/end field.
 *
 * Targets: audio, playIcon, pauseIcon, time (mm:ss.mmm), ms (raw integer)
 * Values:  startMs, endMs (the hook window, in source-track ms)
 */
export default class extends Controller {
  static targets = ["audio", "playIcon", "pauseIcon", "time", "ms"]
  static values = { startMs: Number, endMs: Number }

  connect() {
    this.playing = false
    this.onTimeUpdate = this.onTimeUpdate.bind(this)
    if (this.hasAudioTarget) {
      this.audioTarget.addEventListener("timeupdate", this.onTimeUpdate)
      this.audioTarget.addEventListener("ended", () => this.stop())
    }
    this.render(0)
  }

  disconnect() {
    if (this.hasAudioTarget) {
      this.audioTarget.removeEventListener("timeupdate", this.onTimeUpdate)
    }
  }

  toggle() {
    this.playing ? this.pause() : this.play()
  }

  async play() {
    if (!this.hasAudioTarget) return
    // Always (re)enter at the clip start so the clock reads from 0.
    if (this.clipMs() <= 0 || this.atOrPastEnd()) {
      this.audioTarget.currentTime = this.startSeconds()
    }
    try {
      await this.audioTarget.play()
      this.playing = true
      this.updateIcons()
    } catch (error) {
      console.error("Clip preview failed to play:", error)
      this.playing = false
      this.updateIcons()
    }
  }

  pause() {
    if (!this.hasAudioTarget) return
    this.audioTarget.pause()
    this.playing = false
    this.updateIcons()
  }

  // Stop and rewind to the clip start (clock back to 0).
  stop() {
    if (!this.hasAudioTarget) return
    this.audioTarget.pause()
    this.audioTarget.currentTime = this.startSeconds()
    this.playing = false
    this.updateIcons()
    this.render(0)
  }

  onTimeUpdate() {
    const clip = this.clipMs()
    // Loop the window so the section can be re-heard without fiddling.
    if (this.endMsValue > this.startMsValue && this.atOrPastEnd()) {
      this.audioTarget.currentTime = this.startSeconds()
      this.render(0)
      return
    }
    this.render(clip)
  }

  // Copy the current clip-relative ms to the clipboard (paste into start/end).
  async copyMs() {
    const value = String(this.clipMs())
    try {
      await navigator.clipboard.writeText(value)
      this.flash()
    } catch (error) {
      console.error("Could not copy clip time:", error)
    }
  }

  // ---- helpers ----

  startSeconds() {
    return Math.max(0, this.startMsValue) / 1000
  }

  clipMs() {
    if (!this.hasAudioTarget) return 0
    return Math.max(0, Math.round(this.audioTarget.currentTime * 1000) - this.startMsValue)
  }

  atOrPastEnd() {
    return Math.round(this.audioTarget.currentTime * 1000) >= this.endMsValue
  }

  render(clipMs) {
    if (this.hasMsTarget) this.msTarget.textContent = String(clipMs)
    if (this.hasTimeTarget) this.timeTarget.textContent = this.format(clipMs)
  }

  format(totalMs) {
    const m = Math.floor(totalMs / 60000)
    const s = Math.floor((totalMs % 60000) / 1000)
    const ms = totalMs % 1000
    return `${m}:${String(s).padStart(2, "0")}.${String(ms).padStart(3, "0")}`
  }

  updateIcons() {
    if (this.hasPlayIconTarget) this.playIconTarget.classList.toggle("hidden", this.playing)
    if (this.hasPauseIconTarget) this.pauseIconTarget.classList.toggle("hidden", !this.playing)
  }

  flash() {
    if (!this.hasMsTarget) return
    const el = this.msTarget
    const prev = el.style.color
    el.style.color = "#34d399"
    setTimeout(() => { el.style.color = prev }, 350)
  }
}
